import { isSameDay, parseISO, addMonths } from 'date-fns';
import PropTypes from 'prop-types';
import { createContext, useMemo } from 'react';
import useReportController from '../hooks/useReportController';
import { getBalanceReport } from '../services/reports';
import { DEFAULT_PRODUCT_CURRENCY_CODE, DOLARIZATION_SUPPORTED_CURRENCIES } from '../utils/currency';
import { fDate } from '../utils/formatTime';

export const ReportBalanceContext = createContext({
  table: {},
  balanceReport: [],
  countBalance: 1,
  setType: () => {},
  dates: {},
  setDates: () => {},
  currency: DEFAULT_PRODUCT_CURRENCY_CODE,
  setCurrency: () => {},
  currencyOptions: DOLARIZATION_SUPPORTED_CURRENCIES,
  hasMultiCurrencyFeature: false,
  mutateExport: () => {},
  loadingExport: false,
  fetchingBalance: false,
});

const getDefaultBalanceDates = () => ({
  startDate: new Date(),
  endDate: addMonths(new Date(), 1),
});

const formatBalanceDates = (dates) => ({
  createdAt__gte: fDate(dates?.startDate || new Date(), 'yyyy-MM-dd'),
  createdAt__lte: fDate(dates?.endDate || new Date(), 'yyyy-MM-dd'),
});

const groupBalanceByDay = (data) =>
  data
    ?.reduce((acc, item) => {
      const itemDate = parseISO(item.date);

      const existingDate = acc.find((dateGroup) => isSameDay(parseISO(dateGroup.date), itemDate));

      if (existingDate) {
        existingDate.pending += item.pending;
        existingDate.pendingReserve += item.pendingReserve;
        existingDate.chargeback += item.chargeback || 0;
        existingDate.refunded += item.refunded || 0;
        existingDate.med += item.med || 0;
        existingDate.totalWithoutRefunds = item.totalWithoutRefunds || 0;
        existingDate.paidAmount = item.paidAmount || 0;
        if (item.currency) {
          existingDate.currency = item.currency;
        }
      } else {
        acc.push({
          date: item.date,
          pending: item.pending,
          pendingReserve: item.pendingReserve,
          chargeback: item.chargeback,
          refunded: item.refunded,
          med: item.med,
          totalWithoutRefunds: item.totalWithoutRefunds,
          paidAmount: item.paidAmount,
          currency: item.currency,
        });
      }

      return acc;
    }, [])
    .sort((a, b) => new Date(a.date) - new Date(b.date)) || [];

const ReportBalanceProvider = ({ children }) => {
  const {
    table,
    data: reports,
    fetching: fetchingBalance,
    dates,
    setDates,
    type,
    setType,
    currency,
    setCurrency,
    currencyOptions,
    hasMultiCurrencyFeature,
    mutateExport,
    loadingExport,
  } = useReportController({
    report: 'pending_balance',
    getDefaultDates: getDefaultBalanceDates,
    formatDates: formatBalanceDates,
    tableFilterOptions: {
      tableProps: {
        defaultCurrentPage: 1,
        defaultRowsPerPage: 100,
      },
    },
    defaultType: 'received',
    buildQueryKey: ({ page, type: currentType, rawDates, currency: apiCurrency }) => [
      'reports/pending_balance',
      page,
      currentType,
      rawDates,
      apiCurrency,
    ],
    fetchReport: ({ page, dates: formattedDates, currency: apiCurrency }) =>
      getBalanceReport({
        page,
        status: 'pending',
        dates: formattedDates,
        currency: apiCurrency,
      }),
    select: groupBalanceByDay,
    buildExportParams: (extension) => ({ extension }),
  });

  const value = useMemo(
    () => ({
      balanceReport: reports || [],
      countBalance: reports?.length || 0,
      fetchingBalance,
      table,
      type,
      setType,
      dates,
      setDates,
      currency,
      setCurrency,
      currencyOptions,
      hasMultiCurrencyFeature,
      mutateExport,
      loadingExport,
    }),
    [
      reports,
      fetchingBalance,
      table,
      type,
      setType,
      dates,
      setDates,
      currency,
      setCurrency,
      currencyOptions,
      hasMultiCurrencyFeature,
      mutateExport,
      loadingExport,
    ]
  );

  return <ReportBalanceContext.Provider value={value}>{children}</ReportBalanceContext.Provider>;
};

ReportBalanceProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ReportBalanceProvider;
